import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BellRing, CreditCard, FileText, ShieldAlert } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { publicApi } from "@/lib/api";

const orderHighlights = [
  {
    title: "Fälligkeit und Freigabe",
    copy: "Die Beitragspflicht entsteht erst nach positiver Aufnahmeentscheidung und administrativer Freigabe im Verfahren.",
    icon: CreditCard,
  },
  {
    title: "Mahnung und Nachfrist",
    copy: "Zahlungsverzug wird dokumentiert; Mahnung und Nachfrist erfolgen geordnet und nachvollziehbar.",
    icon: BellRing,
  },
  {
    title: "Ruhen von Rechten",
    copy: "Bei fortbestehendem Rückstand können Sichtbarkeiten, Kennzeichnungen und einzelne Rechte eingeschränkt werden.",
    icon: ShieldAlert,
  },
  {
    title: "Verwaltungsdokumentation",
    copy: "Freigaben, Statusänderungen und Abschlussvermerke bleiben vollständig im System dokumentiert.",
    icon: FileText,
  },
];

export default function ContributionPage({ session, onLogout }) {
  const [content, setContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      const data = await publicApi.getContent();
      setContent(data);
    };
    load();
  }, []);

  const contributionDoc = useMemo(
    () => (content?.legal || []).find((doc) => doc.slug === "beitragsordnung"),
    [content],
  );

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Beitragsordnung"
      pageDescription="Beitragsordnung mit Fälligkeit, Freigabe, Mahnung und Beitragsführung bei REEXPERTS."
    >
      <div className="bg-[#111111] text-[#f7f1e8]">
        <div className="mx-auto flex max-w-7xl flex-col gap-12 px-6 py-14 lg:px-8 lg:py-20">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="contribution-hero-section">
            <div className="space-y-6 rounded-[2rem] border border-[#d4af37]/25 bg-[#141414] p-8">
              <Badge className="w-fit rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]" data-testid="contribution-hero-badge">
                <span className="mr-3 inline-flex items-center gap-1">
                  <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                  <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                  <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
                </span>
                Beitragsordnung
              </Badge>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#b99d6b]" data-testid="contribution-hero-kicker">Fälligkeit · Mahnung · Freigabe · Verwaltungsablauf</p>
                <h1 className="mt-4 font-serif text-4xl leading-[1.04] text-white lg:text-5xl" data-testid="contribution-hero-heading">
                  Die Ordnung der Beitragsverwaltung innerhalb der Vereinigung.
                </h1>
              </div>
              <p className="text-base leading-relaxed text-[#cbc2b7]" data-testid="contribution-hero-description">
                Die Beitragsordnung regelt Entstehung, Fälligkeit, Freigabe, Mahnung, Folgen des Zahlungsrückstands und die systemseitige Dokumentation. Sie ergänzt Satzung und Verfahrensordnung um die operative Beitragsführung der Vereinigung.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="contribution-hero-apply-link">
                  <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]">Mitgliedschaft beantragen</Button>
                </Link>
                <Link to="/rechtliches" data-testid="contribution-hero-legal-link">
                  <Button variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5">Zur Rechtsseite</Button>
                </Link>
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {orderHighlights.map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="border-white/10 bg-[#171717]" data-testid={`contribution-highlight-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <CardContent className="space-y-4 p-6">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-sm leading-relaxed text-[#c7bfb3]">{item.copy}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.section>

          {contributionDoc && (
            <Card className="border-white/10 bg-[#171717]" data-testid="contribution-document-card">
              <CardContent className="space-y-4 p-8">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#b99d6b]" data-testid="contribution-document-kicker">Ordnungstext</p>
                <h2 className="font-serif text-3xl text-white" data-testid="contribution-document-title">{contributionDoc.title}</h2>
                <div className="flex items-center gap-2" data-testid="contribution-document-flag">
                  <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                  <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                  <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
                </div>
                <p className="whitespace-pre-line text-sm leading-8 text-[#ded6ca]" data-testid="contribution-document-content">
                  {contributionDoc.content}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
